import { useState, useEffect, useRef } from "react";
import axios from "axios";
import config from "../../../../config";
import i18n from "i18n";

const API_URL = config.TRAFFIC;

// Helper to format seconds as "X hr Y min Z sec"
function formatTime(value) {
  if (value == null || isNaN(value)) return "";
  const isNeg = value < 0;
  const abs = Math.abs(value);
  const [minPart, secPart] = abs.toString().split(".");
  const totalMins = parseInt(minPart, 10);
  const secs = secPart ? parseInt(secPart.padEnd(2, "0"), 10) : 0;
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  let result = isNeg ? "-" : "";
  if (hrs > 0) result += `${hrs} hr `;
  if (mins > 0 || hrs > 0) result += `${mins} min `;
  result += `${secs} sec`;
  return result.trim();
}

// Transform raw API data into AreaView-friendly shape
// Transform raw API data into AreaView-friendly shape
function transformAPI(apiData) {
  const list = apiData.map((d) => ({
    id: d.id_area === 100 ? 100 : d.id,
    area_id: d.id_area,
title: d.id_area === 100
  ? i18n.t("trafficMonitoring.monitoring.allAreas")
  : (d.name ?? `Area ${d.id_area}`),
    // title: d.name ?? `Area ${d.id_area}`,
    metrics: [
      {
        name: i18n.t("trafficMonitoring.monitoring.metrics.avgAreaSpeed.name"),
        values: [
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.current"),
            value: `${Math.round(d.avg_speed_current)} km/h`,
            status:
              d.avg_speed_current > d.avg_speed_typical ? "pass" : "notPass",
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.typical"),
            value: `${Math.round(d.avg_speed_typical)} km/h`,
            status: "neutral",
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.forecasted"),
            value: d.avg_speed_forecasted
              ? `${Math.round(d.avg_speed_forecasted)} km/h`
              : "",
            status: "neutral",
          },
        ],
        description: i18n.t("trafficMonitoring.monitoring.metrics.avgAreaSpeed.description"),
      },
      {
        name: i18n.t("trafficMonitoring.monitoring.metrics.avgAreaDelay.name"),
        values: [
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.delay"),
            value: formatTime(d.delay),
            // status: d.delay > 0 ? "pass" : "notPass",
             status: "notPass",// always red
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.forecasted"),
            value: d.delay_forecasted ? formatTime(d.delay_forecasted) : "",
            status: "neutral",
          },
        ],
        description: i18n.t("trafficMonitoring.monitoring.metrics.avgAreaDelay.description"),
      },
      {
        name: i18n.t("trafficMonitoring.monitoring.metrics.avgAreaTravelTime.name"),
        values: [
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.current"),
            value: formatTime(d.tt_current),
            status: d.tt_typical > d.tt_current ? "pass" : "notPass",
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.typical"),
            value: formatTime(d.tt_typical),
            status: "neutral",
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.forecasted"),
            value: d.tt_forecasted ? formatTime(d.tt_forecasted) : "",
            status: "neutral",
          },
        ],
        description: i18n.t("trafficMonitoring.monitoring.metrics.avgAreaTravelTime.description"),
      },
      {
        name: i18n.t("trafficMonitoring.monitoring.metrics.congestion.name"),
        values: [
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.current"),
            value: `${d.congestion_current}%`,
            // status:
            //   d.congestion_current > d.congestion_typical ? "pass" : "notPass",
             status: "notPass",// always red
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.typical"),
            value: `${Math.round(d.congestion_typical)}%`,
            status: "neutral",
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.forecasted"),
            value: d.congestion_forecasted
              ? `${Math.round(d.congestion_forecasted)}%`
              : "",
            status: "neutral",
          },
        ],
        description: i18n.t("trafficMonitoring.monitoring.metrics.congestion.description"),
      },
    ],
  }));

  // Move the “all areas” entry (area_id === 100) to the front
  const idx = list.findIndex((a) => a.area_id === 100);
  if (idx > 0) {
    const [allArea] = list.splice(idx, 1);
    list.unshift(allArea);
  }

  return list;
}

const useAreaData = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const isFirstLoad = useRef(true);
  const intervalId = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      const start = new Date();
      // console.log(`[${start.toLocaleTimeString()}] ⏳ Fetching area data…`);
      try {
        const res = await axios.get(`${API_URL}/latest_traffic_data_per_area`, {
          headers: { "Accept-Profile": "traffic_monitoring" },
        });
        const formatted = transformAPI(res.data);
        setData(formatted);
        // console.log(`[${new Date().toLocaleTimeString()}] 📦 Area data:`, formatted);
      } catch (err) {
        console.error(
          `[${new Date().toLocaleTimeString()}] ❌ Error fetching area data:`,
          err
        );
      } finally {
        const end = new Date();
        // console.log(
        //   `[${end.toLocaleTimeString()}] ✅ Fetch took ${((end - start) / 1000).toFixed(2)}s`
        // );
        if (isFirstLoad.current) {
          setLoading(false);
          isFirstLoad.current = false;
        }
      }
    };

    // Initial + polling every minute
    fetchData();
    intervalId.current = setInterval(fetchData, 60000);
    return () => clearInterval(intervalId.current);
  }, []);

  return { data, loading };
};

export default useAreaData;
